import { Fragment, useCallback, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { api, ApiError } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import { useCountry } from '@/lib/country'
import { isoDate, money, shortDate } from '@/lib/format'
import { useAsync } from '@/lib/useAsync'
import { useUnsavedChanges } from '@/lib/useUnsavedChanges'
import { AuditLogPanel } from '@/components/AuditLog'
import { LeaveGuard, useLeaveGuard } from '@/components/LeaveGuard'
import { ProductCommission } from '@/components/ProductCommission'
import { PageHeader, Tabs } from '@/components/ui/page'
import { Badge, Busy, Button, Card, CardHeader, EmptyState, ErrorState, Loading } from '@/components/ui/primitives'
import { Dialog } from '@/components/ui/dialog'
import { Field, FormGrid, Input, MoneyInput, SaveActions, Select, Textarea } from '@/components/ui/form'
import { RichTextEditor } from '@/components/ui/richtext'
import { DEFAULT_PAGE_SIZE, FilterSelect, ListToolbar, Pagination } from '@/components/ui/listing'
import { SortableTh, Table, Td, Th, Tr } from '@/components/ui/table'
import { useToast } from '@/components/ui/toast'
import type {
  BillingType,
  CountryPrice,
  Paged,
  PriceItemDetail,
  ProductDetail,
  ProductListItem,
} from '@/types/api'

/**
 * Where a header's third click lands, and what the toolbar shows for it.
 * A real sort string rather than an empty one, so the two controls always agree.
 *
 * The name, not the catalogue order: nothing sets a product's sort order, so ordering by it
 * put the list in whatever order the database returned and left somebody looking for
 * "HalalSuite PRO" reading every row to find it.
 */
const DEFAULT_SORT = 'name'

export function Products() {
  const { can } = useAuth()
  const [search, setSearch] = useState('')
  const [includeArchived, setIncludeArchived] = useState(false)
  const [sort, setSort] = useState(DEFAULT_SORT)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [creating, setCreating] = useState(false)
  const navigate = useNavigate()
  const toast = useToast()

  const list = useAsync<Paged<ProductListItem>>(
    (signal) =>
      api.get<Paged<ProductListItem>>(
        '/products',
        { search, includeArchived, sort, page, pageSize },
        signal,
      ),
    [search, includeArchived, sort, page, pageSize],
  )

  function refine(apply: () => void) {
    apply()
    setPage(1)
  }

  return (
    <>
      <PageHeader
        eyebrow="Master data"
        title="Products"
        hint="A product is a solution block: a narrative, module bullets and a pricing ladder. Prices are per country and dated."
        actions={
          can('product.create') && (
            <Button variant="primary" onClick={() => setCreating(true)}>
              New product
            </Button>
          )
        }
      />

      <ListToolbar
        sort={sort}
        onSortChange={(next) => refine(() => setSort(next))}
        sortChoices={[
          { value: 'order', label: 'Catalogue order' },
          { value: 'name', label: 'Name A–Z' },
          { value: '-name', label: 'Name Z–A' },
          { value: 'sku', label: 'SKU' },
          { value: 'category', label: 'Category' },
          { value: '-created', label: 'Newest first' },
        ]}
      >
        <div className="w-64">
          <Input
            placeholder="Search products"
            value={search}
            onChange={(e) => refine(() => setSearch(e.target.value))}
          />
        </div>

        <FilterSelect
          label="Archived"
          value={includeArchived ? 'all' : 'live'}
          onChange={(next) => refine(() => setIncludeArchived(next === 'all'))}
        >
          <option value="live">Live only</option>
          <option value="all">Live and archived</option>
        </FilterSelect>
      </ListToolbar>

      {list.error && <ErrorState title={list.error.title} detail={list.error.detail} onRetry={list.reload} />}

      <Busy busy={list.loading}>
        {list.data && (
          <Card>
            {list.data.items.length === 0 ? (
              <EmptyState title="No products yet" hint="The catalogue is what a quotation is built from." />
            ) : (
              <Table>
                <thead>
                  <tr>
                    <SortableTh field="name" sort={sort} onSortChange={(next) => refine(() => setSort(next || DEFAULT_SORT))}>
                      Product
                    </SortableTh>
                    <SortableTh field="category" sort={sort} onSortChange={(next) => refine(() => setSort(next || DEFAULT_SORT))}>
                      Category
                    </SortableTh>
                    <Th numeric>Price items</Th>
                    <Th>Ready to quote</Th>
                  </tr>
                </thead>
                <tbody>
                  {list.data.items.map((product) => (
                    <Tr key={product.id} onClick={() => navigate(`/products/${product.id}`)}>
                      <Td>
                        <span className="font-mono text-[12px] text-muted">{product.sku}</span>
                        <span className="ml-2 font-medium">{product.name}</span>
                        {product.summaryLine && (
                          <span className="block text-[12px] text-muted">{product.summaryLine}</span>
                        )}
                      </Td>
                      <Td className="text-muted">{product.category ?? '-'}</Td>
                      <Td numeric>{product.priceItemCount}</Td>
                      <Td>
                        {product.readyToQuote ? (
                          <Badge tone="positive">Priced</Badge>
                        ) : (
                          <span className="flex flex-col gap-1">
                            <Badge tone="warning">Missing prices</Badge>
                            <span className="text-[12px] text-muted">
                              {product.missingPriceCount} item
                              {product.missingPriceCount === 1 ? '' : 's'} with no price in one or
                              more countries
                            </span>
                          </span>
                        )}
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            )}

            <Pagination
              page={list.data.page}
              pageSize={list.data.pageSize}
              totalCount={list.data.totalCount}
              totalPages={list.data.totalPages}
              onPageChange={setPage}
              onPageSizeChange={(next) => refine(() => setPageSize(next))}
            />
          </Card>
        )}
      </Busy>

      <NewProduct
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={(name) => {
          setCreating(false)
          toast.show(`${name} added`, 'success')
          list.reload()
        }}
      />
    </>
  )
}

/**
 * A product at its own address.
 *
 * <p>
 * It used to be a piece of the listing's state, which meant the URL never changed when one
 * was opened. Nothing could link to a product, the browser's back button left the page
 * entirely, and clicking the highlighted Products item in the sidebar did nothing at all -
 * the router was already where it was being asked to go.
 * </p>
 */
export function ProductDetailPage() {
  const { id } = useParams()
  return id ? (
    <Product productId={id} />
  ) : (
    <ErrorState title="No product" detail="That link is missing a product id." />
  )
}

/** What the details form edits, and therefore what an unsaved change can be about. */
const EDITED = ['name', 'tradeName', 'category', 'summaryLine', 'narrativeHtml', 'modules'] as const

function Product({ productId }: { productId: string }) {
  const navigate = useNavigate()
  const { can } = useAuth()
  const toast = useToast()
  const [form, setForm] = useState<Partial<ProductDetail>>({})
  const [modules, setModules] = useState('')
  const [saving, setSaving] = useState(false)
  // A pricing action always names the country it is for. It used to default to the
  // country context at mount, which on a hard reload of this page is still undefined -
  // leaving an empty country id, an empty price history, and a Set price button whose
  // handler returned before doing anything.
  const [pricing, setPricing] = useState<{ item: PriceItemDetail; countryId: string } | undefined>()
  const [correcting, setCorrecting] = useState<string | undefined>()
  const [addingItem, setAddingItem] = useState(false)
  const [cloning, setCloning] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [archiving, setArchiving] = useState(false)
  const [editingItem, setEditingItem] = useState<PriceItemDetail | undefined>()
  const [removingItem, setRemovingItem] = useState<PriceItemDetail | undefined>()
  const [withdrawing, setWithdrawing] = useState<{ item: PriceItemDetail; price: CountryPrice } | undefined>()
  const [removing, setRemoving] = useState(false)
  const [reordering, setReordering] = useState(false)
  const [tab, setTab] = useState<'details' | 'pricing' | 'commission' | 'history'>('details')

  const detail = useAsync<ProductDetail>(
    (signal) =>
      api.get<ProductDetail>(`/products/${productId}`, undefined, signal).then((loaded) => {
        setForm(loaded)
        setModules(loaded.modules.join('\n'))
        return loaded
      }),
    [productId],
  )

  // Modules are typed as one bullet per line and stored as a list, so the comparison is
  // handed the list the save would send. A stray blank line is then not a change.
  const edited = useMemo(
    () => ({ ...form, modules: modules.split('\n').map((line) => line.trim()).filter(Boolean) }),
    [form, modules],
  )

  const apply = useCallback((values: Partial<ProductDetail>) => {
    setForm(values)
    setModules((values.modules ?? []).join('\n'))
  }, [])

  const changes = useUnsavedChanges(edited, detail.data, apply, EDITED)
  const guard = useLeaveGuard(changes)

  function set<K extends keyof ProductDetail>(key: K, value: ProductDetail[K]) {
    setForm((current) => ({ ...current, [key]: value }))
  }

  async function save() {
    setSaving(true)
    try {
      await api.patch(`/products/${productId}`, {
        name: form.name,
        tradeName: form.tradeName,
        category: form.category,
        summaryLine: form.summaryLine,
        narrativeHtml: form.narrativeHtml,
        sortOrder: form.sortOrder,
      })

      await api.put(`/products/${productId}/modules`, {
        // One bullet per line. A textarea beats a repeating field set for something
        // people paste in from a specification document.
        modules: modules.split('\n').map((line) => line.trim()).filter(Boolean),
      })

      // Before the reload, so the page is clean the moment the save lands rather than
      // for as long as the round trip takes.
      changes.accept()
      toast.show('Product saved', 'success')
      detail.reload()
    } catch (cause) {
      toast.show(cause instanceof ApiError ? cause.message : 'Could not save', 'error')
    } finally {
      setSaving(false)
    }
  }

  // Only the very first load replaces the screen. A reload after a save keeps what is
  // already on it and puts the spinner over the top - blanking a page somebody just
  // saved is the same jump as a loading block pushing a listing down.
  if (detail.loading && !detail.data) return <Loading />
  if (detail.error) return <ErrorState title={detail.error.title} detail={detail.error.detail} onRetry={detail.reload} />

  const editable = can('product.edit')

  // Which of the two removals is being described: a price that has already governed a day is
  // ended, one that has not is taken back. The server decides; this only has to say so.
  const todayIso = isoDate()

  /**
   * Moves one item a place up or down the ladder.
   *
   * The whole order is sent, not the moved row's new number: a swap changes two rows, and
   * two requests where one can fail leaves the ladder with a gap or a tie. The order is
   * also what a quotation copies when a whole product is added, so this is what the
   * customer's document prints, not a preference on this screen.
   */
  async function moveItem(index: number, direction: -1 | 1) {
    const order = (detail.data?.priceItems ?? []).map((item) => item.id)
    const to = index + direction
    const moved = order[index]
    const displaced = order[to]
    if (moved === undefined || displaced === undefined) return

    order[index] = displaced
    order[to] = moved

    setReordering(true)
    try {
      await api.put(`/products/${productId}/price-items/order`, { priceItemIdsInOrder: order })
      detail.reload()
    } catch (cause) {
      toast.show(
        cause instanceof ApiError ? (cause.detail ?? cause.title) : 'Could not reorder the ladder',
        'error',
      )
    } finally {
      setReordering(false)
    }
  }

  /**
   * Takes a price item off the ladder.
   *
   * Archived, not deleted: quotations already raised carry their own copy of the line and
   * the price it was raised at, and the catalogue is still the record of what this product
   * used to sell. It stops being offered on new quotations, which is what removing it means.
   */
  async function removeItem() {
    if (!removingItem) return

    setRemoving(true)
    try {
      await api.post(`/price-items/${removingItem.id}/archive`, {})
      toast.show(`${removingItem.name} removed`, 'success')
      setRemovingItem(undefined)
      detail.reload()
    } catch (cause) {
      toast.show(
        cause instanceof ApiError ? (cause.detail ?? cause.title) : 'Could not remove that item',
        'error',
      )
    } finally {
      setRemoving(false)
    }
  }

  /** Stops selling one item in one country. The server decides whether the dated row is
      ended or removed - see PriceTimeline.Withdraw. */
  async function withdrawPrice() {
    if (!withdrawing?.price.priceId) return

    setRemoving(true)
    try {
      await api.post(`/prices/${withdrawing.price.priceId}/withdraw`, {})
      toast.show(`${withdrawing.item.name} is no longer priced in ${withdrawing.price.isoCode}`, 'success')
      setWithdrawing(undefined)
      detail.reload()
    } catch (cause) {
      toast.show(
        cause instanceof ApiError ? (cause.detail ?? cause.title) : 'Could not remove that price',
        'error',
      )
    } finally {
      setRemoving(false)
    }
  }

  /**
   * Takes the product off the menu, or puts it back.
   *
   * Not the same as deleting it. A product is withdrawn for a season, a region or a
   * rewrite of its pricing and comes back; quotations already carrying it are untouched
   * either way, because a section copies the name and narrative at insert time.
   */
  async function setStatus(isActive: boolean) {
    setRemoving(true)
    try {
      await api.post(`/products/${productId}/status`, { isActive })
      toast.show(isActive ? 'Product is active' : 'Product is inactive', 'success')
      detail.reload()
    } catch (cause) {
      toast.show(
        cause instanceof ApiError ? (cause.detail ?? cause.title) : 'Could not change the status',
        'error',
      )
    } finally {
      setRemoving(false)
    }
  }

  async function archive() {
    setRemoving(true)
    try {
      await api.post(`/products/${productId}/archive`, {})
      toast.show(`${detail.data?.name ?? 'Product'} archived`, 'success')
      guard.bypass(() => navigate('/products', { replace: true }))
    } catch (cause) {
      toast.show(
        cause instanceof ApiError ? (cause.detail ?? cause.title) : 'Could not archive that product',
        'error',
      )
      setRemoving(false)
    }
  }

  async function remove() {
    setRemoving(true)
    try {
      await api.del(`/products/${productId}`)
      toast.show(`${detail.data?.name ?? 'Product'} deleted`, 'success')
      guard.bypass(() => navigate('/products', { replace: true }))
    } catch (cause) {
      toast.show(
        cause instanceof ApiError ? (cause.detail ?? cause.title) : 'Could not delete that product',
        'error',
      )
      setRemoving(false)
    }
  }

  const active = detail.data?.isActive ?? true
  const archivable = can('product.archive') && !detail.data?.isArchived

  return (
    <>
      <PageHeader
        eyebrow={detail.data?.sku}
        title={form.name ?? 'Product'}
        back={{ label: 'Back to products', onClick: () => guard.confirm(() => navigate('/products')) }}
        actions={
          <>
            {/* Three groups, in the order the quotation uses: what makes something new,
                what changes this one's state, and what destroys it. Before this the row
                read Archive, Delete, Clone, Make inactive - the three ways to retire a
                product split apart with the one that copies it in the middle, and the
                destructive verb flush against it. The wider gap between groups is the
                separator, and a group with nothing in it does not render, because an
                empty div still carries that gap. */}
            {can('product.create') && (
              <div className="flex flex-wrap items-center gap-2">
                <Button size="sm" onClick={() => setCloning(true)}>
                  Clone
                </Button>
              </div>
            )}

            {(editable || archivable) && (
              <div className="ml-2 flex flex-wrap items-center gap-2">
                {/* Two verbs that are not the same thing: inactive stops it being
                    offered and is undone by pressing the button again, archiving
                    retires it for good and keeps it. */}
                {editable && (
                  <Button size="sm" disabled={removing} onClick={() => void setStatus(!active)}>
                    {active ? 'Make inactive' : 'Make active'}
                  </Button>
                )}

                {archivable && (
                  <Button size="sm" onClick={() => setArchiving(true)}>
                    Archive
                  </Button>
                )}
              </div>
            )}

            {/* Deleting takes it off the system altogether. Only a product nobody has
                quoted can be deleted, and the API says so. */}
            {can('product.delete') && (
              <div className="ml-2 flex flex-wrap items-center gap-2">
                <Button size="sm" variant="danger" onClick={() => setDeleting(true)}>
                  Delete
                </Button>
              </div>
            )}
          </>
        }
      />

      {cloning && (
        <CloneProduct
          product={detail.data}
          onClose={() => setCloning(false)}
          onCloned={(id) => {
            setCloning(false)
            guard.bypass(() => navigate(`/products/${id}`))
          }}
        />
      )}

      <Dialog
        open={deleting}
        title={`Delete ${detail.data?.name ?? 'this product'}?`}
        onClose={() => setDeleting(false)}
        footer={
          <>
            <Button size="sm" onClick={() => setDeleting(false)}>
              Keep it
            </Button>
            <Button size="sm" variant="danger" loading={removing} onClick={() => void remove()}>
              Delete
            </Button>
          </>
        }
      >
        <p className="text-[13px] text-ink-2">
          The product, its modules and every price on it are removed from the system for
          good. This cannot be undone.
          {' '}
          A product that has ever been quoted cannot be deleted - archive it instead, and
          the quotations that sold it keep their link to it.
        </p>
      </Dialog>

      <Dialog
        open={archiving}
        title={`Archive ${detail.data?.name ?? 'this product'}?`}
        onClose={() => setArchiving(false)}
        footer={
          <>
            <Button size="sm" onClick={() => setArchiving(false)}>
              Keep it live
            </Button>
            <Button size="sm" variant="danger" loading={removing} onClick={() => void archive()}>
              Archive
            </Button>
          </>
        }
      >
        <p className="text-[13px] text-ink-2">
          It stops being offered and leaves the catalogue, but the record stays. Quotations
          that sold it keep their own copy of the name, the narrative and every price they
          were raised at, and go on rendering exactly as they do now.
          {active && ' To stop offering it for a while instead, make it inactive.'}
        </p>
      </Dialog>

      {/* State, not a verb: what it is now reads before what you can do about it - and
          on its own line, because a chip standing in a row of buttons reads as one more
          button. The quotation says what it is in the same place. */}
      <div className="mb-5 flex flex-wrap items-center gap-2">
        <Badge tone={active ? 'positive' : 'neutral'}>{active ? 'Active' : 'Inactive'}</Badge>
        {detail.data?.isArchived && <Badge tone="warning">Archived</Badge>}
      </div>

      <Tabs
        active={tab}
        onSelect={setTab}
        tabs={[
          { id: 'details', label: 'Details' },
          { id: 'pricing', label: 'Pricing', count: detail.data?.priceItems.length },

          // Absent, not disabled, for anybody without the permission. A standard rate is a
          // commission figure, and commission is invisible below the Director of Sales.
          ...(can('reseller.commission.view')
            ? [{ id: 'commission' as const, label: 'Commission' }]
            : []),
          { id: 'history', label: 'History' },
        ]}
      />

      {tab === 'details' && (
      <Card className="mb-5">
        <CardHeader title="What it is" hint="This text becomes the solution block on the quotation." />
        <FormGrid>
          <Field label="Name" required changed={changes.changed('name')}>
            {(id) => (
              <Input id={id} disabled={!editable} value={form.name ?? ''} onChange={(e) => set('name', e.target.value)} />
            )}
          </Field>
          <Field
            label="Trade name"
            changed={changes.changed('tradeName')}
            hint="Printed instead of the internal name, when they differ.">
            {(id) => (
              <Input
                id={id}
                disabled={!editable}
                value={form.tradeName ?? ''}
                onChange={(e) => set('tradeName', e.target.value)}
              />
            )}
          </Field>
          <Field label="Category" changed={changes.changed('category')}>
            {(id) => (
              <Input
                id={id}
                disabled={!editable}
                value={form.category ?? ''}
                onChange={(e) => set('category', e.target.value)}
              />
            )}
          </Field>
          <Field
            label="Summary line"
            changed={changes.changed('summaryLine')}
            hint="One sentence under the block title.">
            {(id) => (
              <Input
                id={id}
                disabled={!editable}
                value={form.summaryLine ?? ''}
                onChange={(e) => set('summaryLine', e.target.value)}
              />
            )}
          </Field>
          <Field
            label="Narrative"
            changed={changes.changed('narrativeHtml')}
            hint="What the customer reads under the block title. Headings, lists, colour and links print as they look here. Copied onto a quotation and editable there.">
            {(id) => (
              <RichTextEditor
                id={id}
                disabled={!editable}
                minHeight="9rem"
                value={form.narrativeHtml}
                placeholder="What this product is, and what the customer gets."
                onChange={(html) => set('narrativeHtml', html)}
              />
            )}
          </Field>
          <Field label="Modules" changed={changes.changed('modules')} hint="One bullet per line.">
            {(id) => (
              <Textarea
                id={id}
                disabled={!editable}
                rows={6}
                value={modules}
                onChange={(e) => setModules(e.target.value)}
              />
            )}
          </Field>
        </FormGrid>
        {editable && (
          <SaveActions
            count={changes.fields.length}
            saving={saving}
            saveLabel="Save product"
            canSave={Boolean(form.name?.trim())}
            onSave={() => void save()}
            onDiscard={changes.discard}
          />
        )}
      </Card>
      )}

      {tab === 'pricing' && (
      <Card>
        <CardHeader
          title="Pricing ladder"
          hint="One-time, monthly and annual lines. Every country you have access to gets a row, priced or not."
          action={
            editable && (
              <Button size="sm" onClick={() => setAddingItem(true)}>
                Add price item
              </Button>
            )
          }
        />
        {detail.data && detail.data.priceItems.length === 0 ? (
          <EmptyState
            title="Nothing priced yet"
            hint="A product with no price items cannot appear on a quotation."
          />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Item</Th>
                <Th>Billing / country</Th>
                <Th>Unit</Th>
                <Th numeric>Price</Th>
                <Th>Effective</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {detail.data?.priceItems.map((item, index) => (
                <Fragment key={item.id}>
                  <tr>
                    <Td>
                      <span className="font-medium">{item.name}</span>
                    </Td>
                    <Td>
                      <Badge tone={item.billingType === 'OneTime' ? 'neutral' : 'accent'}>
                        {item.billingType === 'OneTime' ? 'One-time' : item.billingType}
                      </Badge>
                    </Td>
                    <Td className="text-muted">{item.uom}</Td>
                    <Td colSpan={2} />
                    <Td>
                      {editable && (
                        <span className="flex justify-end gap-2">
                          {/* The ladder's order is the order the lines print in, so it is
                              moved here rather than left to whichever row was added first. */}
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={reordering || index === 0}
                            onClick={() => void moveItem(index, -1)}
                            aria-label={`Move ${item.name} up`}
                          >
                            ↑
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={reordering || index === (detail.data?.priceItems.length ?? 0) - 1}
                            onClick={() => void moveItem(index, 1)}
                            aria-label={`Move ${item.name} down`}
                          >
                            ↓
                          </Button>
                          <Button size="sm" onClick={() => setEditingItem(item)}>
                            Edit
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setRemovingItem(item)}>
                            Remove
                          </Button>
                        </span>
                      )}
                    </Td>
                  </tr>

                  {/* One row per country the viewer may price, priced or not. The country
                      question answers itself here: an unpriced country is a visible row
                      saying so, rather than something you have to open a dialog to find. */}
                  {item.prices.map((price) =>
                    correcting === price.priceId && price.priceId ? (
                      <tr key={price.countryId}>
                        <Td />
                        <Td colSpan={5}>
                          <CorrectPrice
                            price={price}
                            onCancel={() => setCorrecting(undefined)}
                            onSaved={() => {
                              setCorrecting(undefined)
                              detail.reload()
                            }}
                          />
                        </Td>
                      </tr>
                    ) : (
                      <tr key={price.countryId}>
                        <Td />
                        <Td className="font-mono text-[11px] text-muted">{price.isoCode}</Td>
                        <Td className="text-[12px] text-muted">
                          {price.minQuantity !== null && price.minQuantity > 1
                            ? `min ${price.minQuantity}`
                            : ''}
                        </Td>
                        <Td numeric>
                          {price.isPriced && price.unitPrice !== null ? (
                            money(price.unitPrice, price.currencyCode, 2)
                          ) : (
                            <span className="text-warn">not priced</span>
                          )}
                        </Td>
                        <Td className="text-[12px] text-muted">
                          {price.effectiveFrom ? `from ${shortDate(price.effectiveFrom)}` : '-'}
                        </Td>
                        <Td>
                          {can('product.price.edit') && (
                            <span className="flex justify-end gap-2">
                              {price.priceId && (
                                <Button size="sm" onClick={() => setCorrecting(price.priceId!)}>
                                  Edit
                                </Button>
                              )}
                              <Button
                                size="sm"
                                onClick={() => setPricing({ item, countryId: price.countryId })}
                              >
                                {price.isPriced ? 'New price' : 'Set price'}
                              </Button>
                              {price.priceId && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => setWithdrawing({ item, price })}
                                >
                                  Remove
                                </Button>
                              )}
                            </span>
                          )}
                        </Td>
                      </tr>
                    ),
                  )}
                </Fragment>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
      )}

      {tab === 'commission' && <ProductCommission productId={productId} />}

      {tab === 'history' && (
        <AuditLogPanel entityType="Product" entityId={productId} className="" />
      )}

      {/* Mounted only while it is open, so its fields start from the item it was opened for
          rather than from whichever was edited last. */}
      {editingItem && (
        <EditPriceItem
          item={editingItem}
          onClose={() => setEditingItem(undefined)}
          onSaved={() => {
            setEditingItem(undefined)
            detail.reload()
          }}
        />
      )}

      <Dialog
        open={removingItem !== undefined}
        title={removingItem ? `Remove ${removingItem.name}?` : 'Remove this item?'}
        onClose={() => setRemovingItem(undefined)}
        footer={
          <>
            <Button size="sm" onClick={() => setRemovingItem(undefined)}>
              Keep it
            </Button>
            <Button size="sm" variant="danger" loading={removing} onClick={() => void removeItem()}>
              Remove
            </Button>
          </>
        }
      >
        <p className="text-[13px] text-ink-2">
          It stops being offered on new quotations, in every country. Quotations already
          raised keep their own copy of the line at the price they were raised at, and its
          price history is kept.
        </p>
      </Dialog>

      <Dialog
        open={withdrawing !== undefined}
        title={
          withdrawing
            ? `Stop pricing ${withdrawing.item.name} in ${withdrawing.price.isoCode}?`
            : 'Remove this price?'
        }
        onClose={() => setWithdrawing(undefined)}
        footer={
          <>
            <Button size="sm" onClick={() => setWithdrawing(undefined)}>
              Keep it
            </Button>
            <Button size="sm" variant="danger" loading={removing} onClick={() => void withdrawPrice()}>
              Remove
            </Button>
          </>
        }
      >
        {/* Two different things, said as two, because they are undone differently. A price
            that has charged somebody is the record of it and is ended rather than erased; one
            that has charged nobody is taken back, and the country returns to the price it was
            on before. */}
        <p className="text-[13px] text-ink-2">
          {withdrawing && withdrawing.price.effectiveFrom && withdrawing.price.effectiveFrom < todayIso
            ? `The country stops being quotable for this item. What ${withdrawing.price.isoCode} was charged up to today is kept as the record of it.`
            : `This price has not applied to a single day yet, so it is taken back and ${withdrawing?.price.isoCode ?? 'the country'} returns to whatever it was priced at before.`}
        </p>
      </Dialog>

      {/* Mounted only while it is open, so the country it was opened for is what its
          form starts from. */}
      {pricing && (
        <SetPrice
          item={pricing.item}
          countryId={pricing.countryId}
          onClose={() => setPricing(undefined)}
          onSaved={() => {
            setPricing(undefined)
            detail.reload()
          }}
        />
      )}

      <NewPriceItem
        open={addingItem}
        productId={productId}
        onClose={() => setAddingItem(false)}
        onCreated={() => {
          setAddingItem(false)
          detail.reload()
        }}
      />

      <LeaveGuard guard={guard} />
    </>
  )
}

/**
 * Adds a dated price for one country.
 *
 * <p>
 * The country arrives from the row that opened this, and can still be changed here -
 * pricing a product for Singapore should not mean leaving the screen, switching country
 * and finding your way back to the same price item.
 * </p>
 *
 * Mounted only while open. It used to live permanently in the tree and take its country
 * from the context bar at mount, which is undefined until the country effect resolves:
 * on a hard reload of this page the country id stayed empty, the history call returned
 * nothing, and the Set price button returned before doing any work.
 */
function SetPrice({
  item,
  countryId: initialCountryId,
  onClose,
  onSaved,
}: {
  item: PriceItemDetail
  countryId: string
  onClose: () => void
  onSaved: () => void
}) {
  const { available } = useCountry()
  const toast = useToast()

  const [countryId, setCountryId] = useState(initialCountryId)
  const country = available.find((c) => c.countryId === countryId)
  const [unitPrice, setUnitPrice] = useState('')
  const [minQuantity, setMinQuantity] = useState('1')
  const [annualDiscount, setAnnualDiscount] = useState('0')
  const [effectiveFrom, setEffectiveFrom] = useState(isoDate())
  const [saving, setSaving] = useState(false)

  const history = useAsync(
    (signal) => api.get<PriceRow[]>(`/price-items/${item.id}/prices`, { countryId }, signal),
    [item.id, countryId],
  )

  async function save() {
    if (!countryId) return

    setSaving(true)
    try {
      await api.put(`/price-items/${item.id}/prices`, {
        countryId,
        unitPrice: Number(unitPrice),
        minQuantity: Number(minQuantity),
        annualDiscountPercent: Number(annualDiscount),
        effectiveFrom,
      })

      toast.show('Price set', 'success')
      onSaved()
    } catch (cause) {
      toast.show(cause instanceof ApiError ? cause.message : 'Could not set the price', 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog
      open
      title={`Price for ${item.name}`}
      description="A price is per country and dated. Quotations already issued keep the price they were raised with."
      onClose={onClose}
      footer={
        <>
          <Button size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" variant="primary" loading={saving} disabled={!unitPrice} onClick={() => void save()}>
            Set price
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Country" hint="Each country has its own price and its own currency.">
          {(id) => (
            <Select id={id} value={countryId} onChange={(e) => setCountryId(e.target.value)}>
              {available.map((option) => (
                <option key={option.countryId} value={option.countryId}>
                  {option.name} · {option.currencyCode}
                </option>
              ))}
            </Select>
          )}
        </Field>

        <Field label={`Unit price (${country?.currencyCode ?? ''})`} required>
          {(id) => <MoneyInput id={id} value={unitPrice} onChange={(e) => setUnitPrice(e.target.value)} />}
        </Field>
        <Field label="Minimum quantity">
          {(id) => (
            <Input id={id} type="number" min="0" value={minQuantity} onChange={(e) => setMinQuantity(e.target.value)} />
          )}
        </Field>
        <Field label="Annual commitment discount %" hint="Applied when the line is billed annually rather than monthly.">
          {(id) => (
            <Input
              id={id}
              type="number"
              step="0.01"
              value={annualDiscount}
              onChange={(e) => setAnnualDiscount(e.target.value)}
            />
          )}
        </Field>
        <Field label="Effective from" hint="The previous price closes the day before this one starts.">
          {(id) => <Input id={id} type="date" value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} />}
        </Field>

        {history.loading && <Loading label="Loading price history..." />}

        {history.data && history.data.length > 0 && (
          <div>
            <p className="mb-1.5 font-mono text-[11px] uppercase tracking-[0.12em] text-muted">
              Price history · {country?.isoCode ?? ''}
            </p>

            {/* Read only. Correcting a price happens on its own row in the ladder, where
                it is one click from the figure being corrected rather than three. */}
            <ul className="flex flex-col gap-1 text-[13px]">
              {history.data.map((entry) => (
                <li key={entry.id} className="flex items-center justify-between gap-3 text-muted">
                  <span className="tabular">{money(entry.unitPrice, entry.currencyCode, 2)}</span>
                  <span>
                    {shortDate(entry.effectiveFrom)} –{' '}
                    {entry.effectiveTo ? shortDate(entry.effectiveTo) : 'current'}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Dialog>
  )
}

interface PriceRow {
  id: string
  isoCode: string
  currencyCode: string
  unitPrice: number
  minQuantity: number
  annualDiscountPercent: number
  effectiveFrom: string
  effectiveTo: string | null
}

/**
 * Corrects the price a country is charging today, in place, on its own row in the ladder.
 *
 * <p>
 * The date is not editable here. It decides which row a given day resolves to, so moving
 * it would have to re-close its neighbours - and charging something different from a new
 * date is a new dated row, which is what "New price" is for.
 * </p>
 */
function CorrectPrice({
  price,
  onCancel,
  onSaved,
}: {
  price: CountryPrice
  onCancel: () => void
  onSaved: () => void
}) {
  const toast = useToast()
  const [unitPrice, setUnitPrice] = useState(String(price.unitPrice ?? ''))
  const [minQuantity, setMinQuantity] = useState(String(price.minQuantity ?? 1))
  const [annualDiscount, setAnnualDiscount] = useState(String(price.annualDiscountPercent ?? 0))
  const [saving, setSaving] = useState(false)

  async function save() {
    setSaving(true)
    try {
      await api.patch(`/prices/${price.priceId}`, {
        unitPrice: Number(unitPrice),
        minQuantity: Number(minQuantity),
        annualDiscountPercent: Number(annualDiscount),
      })

      toast.show('Price corrected', 'success')
      onSaved()
    } catch (cause) {
      toast.show(cause instanceof ApiError ? cause.message : 'Could not correct the price', 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="flex flex-col gap-2 rounded-md border border-line bg-sunken p-3">
      <p className="text-[12px] text-muted">
        {price.isoCode} · {shortDate(price.effectiveFrom)} –{' '}
        {price.effectiveTo ? shortDate(price.effectiveTo) : 'current'}
      </p>

      <div className="grid grid-cols-3 gap-2">
        <Field label={`Price (${price.currencyCode})`}>
          {(id) => <MoneyInput id={id} value={unitPrice} onChange={(e) => setUnitPrice(e.target.value)} />}
        </Field>
        <Field label="Min qty">
          {(id) => (
            <Input id={id} type="number" min="0" value={minQuantity} onChange={(e) => setMinQuantity(e.target.value)} />
          )}
        </Field>
        <Field label="Annual %">
          {(id) => (
            <Input
              id={id}
              type="number"
              step="0.01"
              value={annualDiscount}
              onChange={(e) => setAnnualDiscount(e.target.value)}
            />
          )}
        </Field>
      </div>

      <div className="flex justify-end gap-2">
        <Button size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button size="sm" variant="primary" loading={saving} disabled={!unitPrice} onClick={() => void save()}>
          Save price
        </Button>
      </div>
    </div>
  )
}

/**
 * Corrects what a price item is, rather than what it costs.
 *
 * <p>
 * The name is the line's description on the quotation, the billing type decides which side
 * of the one-time / recurring split it lands on, and the unit is what a quantity is counted
 * in. All three are copied onto a quotation line at insert time, so changing them here
 * changes what the next quotation says and never what an old one claimed.
 * </p>
 */
/**
 * A new product that starts as a copy of this one.
 *
 * <p>
 * The copy carries the narrative, the modules, the price items and what each of them
 * charges today. It does not carry the original's price history - that is the record of
 * what Codemax charged for the original, and this product has never charged anybody
 * anything - and it arrives inactive, because a half-edited copy of a real product turning
 * up in a rep's picker is the accident worth avoiding.
 * </p>
 */
function CloneProduct({
  product,
  onClose,
  onCloned,
}: {
  product: ProductDetail | undefined
  onClose: () => void
  onCloned: (id: string) => void
}) {
  const toast = useToast()
  const [sku, setSku] = useState(product ? `${product.sku}-COPY` : '')
  const [name, setName] = useState(product ? `${product.name} (copy)` : '')
  const [saving, setSaving] = useState(false)

  const items = product?.priceItems.length ?? 0

  async function clone() {
    if (!product) return

    setSaving(true)
    try {
      const created = await api.post<{ id: string }>(`/products/${product.id}/clone`, {
        sku: sku.trim(),
        name: name.trim(),
      })

      toast.show(`${name.trim()} created`, 'success')
      onCloned(created.id)
    } catch (cause) {
      toast.show(
        cause instanceof ApiError ? (cause.detail ?? cause.title) : 'Could not clone that product',
        'error',
      )
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog
      open
      title={`Clone ${product?.name ?? 'this product'}`}
      description="A copy to edit, rather than a second product typed in from scratch."
      onClose={onClose}
      footer={
        <>
          <Button size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            variant="primary"
            loading={saving}
            disabled={!sku.trim() || !name.trim()}
            onClick={() => void clone()}
          >
            Create copy
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Code" required hint="Its own, so the two can be told apart.">
          {(id) => <Input id={id} value={sku} onChange={(e) => setSku(e.target.value)} />}
        </Field>

        <Field label="Name" required>
          {(id) => <Input id={id} value={name} onChange={(e) => setName(e.target.value)} />}
        </Field>

        <p className="rounded-md bg-sunken px-3 py-2 text-[12.5px] text-muted">
          Copies the narrative, the modules and {items} price item{items === 1 ? '' : 's'},
          each priced at what this product charges today. The copy starts inactive, so
          nothing reaches a quotation until you say so.
        </p>
      </div>
    </Dialog>
  )
}

function EditPriceItem({
  item,
  onClose,
  onSaved,
}: {
  item: PriceItemDetail
  onClose: () => void
  onSaved: () => void
}) {
  const toast = useToast()
  const [name, setName] = useState(item.name)
  const [billingType, setBillingType] = useState<BillingType>(item.billingType)
  const [uom, setUom] = useState(item.uom)
  const [saving, setSaving] = useState(false)

  const priced = item.prices.filter((price) => price.isPriced).length

  async function save() {
    setSaving(true)
    try {
      await api.patch(`/price-items/${item.id}`, {
        name: name.trim(),
        billingType,
        uom: uom.trim(),
      })

      toast.show('Price item updated', 'success')
      onSaved()
    } catch (cause) {
      toast.show(
        cause instanceof ApiError ? (cause.detail ?? cause.title) : 'Could not save that item',
        'error',
      )
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog
      open
      title="Edit price item"
      description="What this line is called, how it bills and what it is counted in. Its prices are unchanged."
      onClose={onClose}
      footer={
        <>
          <Button size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            variant="primary"
            loading={saving}
            disabled={!name.trim() || !uom.trim()}
            onClick={() => void save()}
          >
            Save
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Description" required hint="What the customer reads on the quotation line.">
          {(id) => <Input id={id} value={name} onChange={(e) => setName(e.target.value)} />}
        </Field>

        <Field
          label="Billing"
          hint="Drives the one-time versus recurring split on the document, and which payment milestones can divide it."
        >
          {(id) => (
            <Select
              id={id}
              value={billingType}
              onChange={(e) => setBillingType(e.target.value as BillingType)}
            >
              <option value="OneTime">One-time</option>
              <option value="Monthly">Monthly</option>
              <option value="Annual">Annual</option>
            </Select>
          )}
        </Field>

        <Field label="Unit of measure" hint="user, device, outlet, licence.">
          {(id) => <Input id={id} value={uom} onChange={(e) => setUom(e.target.value)} />}
        </Field>

        {billingType !== item.billingType && priced > 0 && (
          <p className="rounded-md bg-warn-wash px-3 py-2 text-[12.5px] text-warn">
            Changing how this bills moves it between the one-time and recurring totals on
            every future quotation. Its {priced} price{priced === 1 ? '' : 's'} are kept as
            they are - a monthly figure read as an annual one is the same number meaning
            twelve times less.
          </p>
        )}
      </div>
    </Dialog>
  )
}

function NewPriceItem({
  open,
  productId,
  onClose,
  onCreated,
}: {
  open: boolean
  productId: string
  onClose: () => void
  onCreated: () => void
}) {
  const toast = useToast()
  const [name, setName] = useState('')
  const [billingType, setBillingType] = useState<BillingType>('OneTime')
  const [uom, setUom] = useState('unit')
  const [saving, setSaving] = useState(false)

  async function create() {
    setSaving(true)
    try {
      // No sort order: the server appends it to the ladder.
      await api.post(`/products/${productId}/price-items`, { name, billingType, uom })
      onCreated()
      setName('')
      setUom('unit')
    } catch (cause) {
      toast.show(cause instanceof ApiError ? cause.message : 'Could not add the item', 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog
      open={open}
      title="Add price item"
      description="A line on the pricing ladder: a licence, a setup fee, a support plan."
      onClose={onClose}
      footer={
        <>
          <Button size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" variant="primary" loading={saving} disabled={!name} onClick={() => void create()}>
            Add item
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Name" required>
          {(id) => <Input id={id} value={name} onChange={(e) => setName(e.target.value)} />}
        </Field>
        <Field label="Billing" hint="Drives the one-time versus recurring split on the document.">
          {(id) => (
            <Select id={id} value={billingType} onChange={(e) => setBillingType(e.target.value as BillingType)}>
              <option value="OneTime">One-time</option>
              <option value="Monthly">Monthly</option>
              <option value="Annual">Annual</option>
            </Select>
          )}
        </Field>
        <Field label="Unit of measure" hint="user, device, outlet, licence.">
          {(id) => <Input id={id} value={uom} onChange={(e) => setUom(e.target.value)} />}
        </Field>
      </div>
    </Dialog>
  )
}

function NewProduct({
  open,
  onClose,
  onCreated,
}: {
  open: boolean
  onClose: () => void
  onCreated: (name: string) => void
}) {
  const toast = useToast()
  const [sku, setSku] = useState('')
  const [name, setName] = useState('')
  const [category, setCategory] = useState('')
  const [saving, setSaving] = useState(false)

  async function create() {
    setSaving(true)
    try {
      await api.post('/products', { sku, name, category: category || null, sortOrder: 0, modules: [] })
      onCreated(name)
      setSku('')
      setName('')
      setCategory('')
    } catch (cause) {
      toast.show(cause instanceof ApiError ? cause.message : 'Could not add the product', 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog
      open={open}
      title="New product"
      description="Products are shared across countries. Their prices are not."
      onClose={onClose}
      footer={
        <>
          <Button size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" variant="primary" loading={saving} disabled={!sku || !name} onClick={() => void create()}>
            Add product
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="SKU" required hint="Unique, and how the catalogue is searched.">
          {(id) => <Input id={id} value={sku} onChange={(e) => setSku(e.target.value)} />}
        </Field>
        <Field label="Name" required>
          {(id) => <Input id={id} value={name} onChange={(e) => setName(e.target.value)} />}
        </Field>
        <Field label="Category">
          {(id) => <Input id={id} value={category} onChange={(e) => setCategory(e.target.value)} />}
        </Field>
      </div>
    </Dialog>
  )
}
